import { createContext, useContext, useEffect, useState } from 'react';
import {
  DashboardsColumnDialogProp,
  DashboardsContextData,
  DashboardsData,
  DashboardsDialogProp,
  DashboardsProviderProps,
} from './types';
import { Emptied } from '@utils/types';
import { initialState } from './constants';
import { useGetDashboardsColumns } from '@services/dashboards/columns';
import { DashboardsColumn, Preset } from '@services/dashboards/types';
import { useGetDashboardsPreview } from '@services/dashboards/preview';
import { useGetPresets } from '@services/dashboards/preset';
import {
  appendId,
  appendPresetColumns,
  parseScope,
  parseSource,
  reconcileColumnIds,
} from './helpers';
import { useQueryParams } from '@hooks/queryParams';

const DashboardsContext = createContext<Emptied<DashboardsContextData>>({});

export const DashboardsProvider = ({ children }: DashboardsProviderProps) => {
  const [state, setState] = useState<DashboardsData>(initialState);

  const {
    params: { search },
  } = useQueryParams();

  const {
    dialog: { column },
    selectedColumns,
    initialColumns,
    sorting,
    source,
  } = state;

  const presets = useGetPresets();

  const columns = useGetDashboardsColumns(parseSource(source), column);

  const preview = useGetDashboardsPreview({
    callback: ({ records }) => {
      if (!records) return;
      setState((prev) => ({ ...prev, preview: records }));
    },
  });

  useEffect(() => {
    if (
      JSON.stringify(selectedColumns) ===
      JSON.stringify(
        appendPresetColumns(
          state.columns.records,
          presets.data?.records?.find((a) => a.id === state.presetId)?.columns,
        ),
      )
    )
      return;
    setState((prev) => ({
      ...prev,
      saved: false,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns]);

  useEffect(() => {
    if (!selectedColumns.length) return;
    preview.mutate({ columns: selectedColumns, sorting, scope: parseSource(state.source) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns, sorting, search]);

  useEffect(() => {
    if (!columns?.data?.records) return;
    const newColumns = appendId(columns.data?.records || [], initialColumns);

    setState((prev) => ({
      ...prev,
      initialColumns: state.columns.records.some((el) => el.uuid)
        ? prev.initialColumns
        : newColumns,
      columns: {
        records: newColumns,
        isLoading: false,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns?.data?.records]);

  useEffect(() => {
    if (!presets.data?.records) return;
    const presetId = !state.presetId
      ? presets.data.records.find((el) => el.defaultView)?.id || ''
      : state.presetId;

    const preset = presets.data?.records.find((a) => a.id === presetId);

    const newColumns = appendPresetColumns(state.columns.records, preset?.columns);

    setState((prev) => ({
      ...prev,
      presetId,
      selectedColumns: newColumns,
      checkedColumns: newColumns,
      sorting: preset?.sorting || prev.sorting,
      source: parseScope(preset?.scope),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.presetId, presets.data?.records]);

  useEffect(() => {
    if (!column.details) return;
    setState((prev) => ({
      ...prev,
      checkedColumns: selectedColumns,
    }));
  }, [column.details, selectedColumns]);

  useEffect(() => {
    if (column.edit || !state.columns.records.length) return;
    setState((prev) => {
      const newSelectedColumns = reconcileColumnIds(prev.selectedColumns, prev.columns.records);
      const newCheckedColumns = reconcileColumnIds(prev.checkedColumns, prev.columns.records);
      if (newSelectedColumns === prev.selectedColumns && newCheckedColumns === prev.checkedColumns)
        return prev;
      return { ...prev, selectedColumns: newSelectedColumns, checkedColumns: newCheckedColumns };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.columns.records, column.edit]);

  useEffect(() => {
    const widthUuids = Object.keys(state.columnWidths);
    if (!widthUuids.length) return;
    const selectedUuids = state.selectedColumns.map((el) => el.uuid);
    const matches =
      selectedUuids.length === widthUuids.length &&
      selectedUuids.every((uuid) => uuid && widthUuids.includes(uuid));
    if (matches) return;
    setState((prev) => ({ ...prev, columnWidths: {} }));
  }, [state.selectedColumns, state.columnWidths]);

  const setPresetDialog = (type: DashboardsDialogProp, value: boolean, values?: Emptied<Preset>) =>
    setState((prev) => ({
      ...prev,
      dialog: {
        ...prev.dialog,
        preset: {
          ...prev.dialog.preset,
          [type]: value,
          values: values || prev.dialog.preset.values,
        },
      },
    }));

  const setColumnDialog = (
    type: DashboardsColumnDialogProp,
    value: boolean,
    values?: Emptied<DashboardsColumn>,
  ) =>
    setState((prev) => ({
      ...prev,
      dialog: {
        ...prev.dialog,
        column: {
          ...prev.dialog.column,
          [type]: value,
          values: values || prev.dialog.column.values,
        },
      },
    }));

  return (
    <DashboardsContext.Provider
      value={{
        ...state,
        isLoading: preview.isLoading,
        presets: {
          records: presets.data?.records || [],
          isLoading: presets.isLoading,
        },
        setPresetDialog,
        setColumnDialog,
        setState,
      }}>
      {children}
    </DashboardsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useDashboards = () => useContext(DashboardsContext);
